import { datadogLogs } from "@datadog/browser-logs";
import { getEnv } from "@src/utils/env-utils";

const DATADOG_CLIENT_TOKEN = "pub52326dd40ea6e1dfc79abf14954a0bfb";
const DATADOG_SITE = "datadoghq.eu";

type Stage = "prod" | "preprod" | "dev";

// Aligné avec les channels utilisés côté backend PHP (monolog).
export enum EDataDogChannel {
  APP = "app",
}

/**
 * Déduit le stage à partir de GATSBY_ENV.
 * L'ordre est important : "preprod" doit être testé avant "prod"
 * car la chaîne "preprod" contient "prod".
 */
const getStageFromEnv = (env: string): Stage => {
  if (env.includes("preprod")) return "preprod";
  if (env.includes("prod")) return "prod";
  return "dev";
};

let initialized = false;

/**
 * Initialise les logs applicatifs DataDog côté client.
 * - Ne fait rien côté SSR (le SDK accède à document/window).
 * - Idempotent : ne s'initialise qu'une seule fois.
 * - Renseigne un contexte global (env, stage, referrer).
 */
export const initDatadogLogs = (): void => {
  if (typeof window === "undefined" || initialized) return;

  const env = getEnv("GATSBY_ENV", "dev");
  const stage = getStageFromEnv(env);

  datadogLogs.init({
    clientToken: DATADOG_CLIENT_TOKEN,
    site: DATADOG_SITE,
    forwardErrorsToLogs: false,
    sessionSampleRate: 100,
  });

  datadogLogs.setGlobalContextProperty("env", env);
  datadogLogs.setGlobalContextProperty("stage", stage);
  datadogLogs.setGlobalContextProperty("referrer", document.referrer);

  initialized = true;
};

const log = (
  level: "warn" | "error" | "notice" | "info" | "debug",
  message: string,
  context?: Record<string, unknown>,
  channel: EDataDogChannel = EDataDogChannel.APP
): void => {
  if (typeof window === "undefined") return;

  datadogLogs.logger[level](
    message,
    context ? { context, channel } : { channel }
  );
};

/**
 * Helpers de log applicatif DataDog, alignés sur la structure des logs PHP backend :
 * attributs métier regroupés sous `context`, `channel` par défaut à "app".
 */
export const dataDogLogger = {
  warn: (
    message: string,
    context?: Record<string, unknown>,
    channel?: EDataDogChannel
  ) => log("warn", message, context, channel),
  error: (
    message: string,
    context?: Record<string, unknown>,
    channel?: EDataDogChannel
  ) => log("error", message, context, channel),
  notice: (
    message: string,
    context?: Record<string, unknown>,
    channel?: EDataDogChannel
  ) => log("notice", message, context, channel),
  info: (
    message: string,
    context?: Record<string, unknown>,
    channel?: EDataDogChannel
  ) => log("info", message, context, channel),
  debug: (
    message: string,
    context?: Record<string, unknown>,
    channel?: EDataDogChannel
  ) => log("debug", message, context, channel),
};

// Exposition du logger pour usage ailleurs dans l'app
export { datadogLogs };
